import { resolvePrice as coreResolvePrice, type PriceRowType } from '@florezjaramillo/florezjaramillo-core'
import type { PaginatedResponseType } from '~/types/api'
import api from './api'
import type { ParameterType } from './parameter.service'

/** Un servicio no se inventaría ni se envía; un producto sí. */
export type ProductKindType = 'service' | 'product'
export type ProductStatusType = 'draft' | 'published' | 'archived'
export type MovementTypeType = 'in' | 'out' | 'adjust'

export const PRODUCT_KIND_OPTIONS: { value: ProductKindType; title: string; subtitle: string }[] = [
  { value: 'product', title: 'Producto', subtitle: 'Algo físico, con existencias' },
  { value: 'service', title: 'Servicio', subtitle: 'Trabajo que se presta, sin inventario' }
]

export const PRODUCT_STATUS_OPTIONS: { value: ProductStatusType; title: string; subtitle: string }[] = [
  { value: 'published', title: 'Publicado', subtitle: 'Se puede cotizar y lo ve el asesor' },
  { value: 'draft', title: 'Borrador', subtitle: 'Sólo lo ves tú mientras lo preparas' },
  { value: 'archived', title: 'Archivado', subtitle: 'Fuera del catálogo, se conserva el historial' }
]

export const MOVEMENT_TYPE_OPTIONS: { value: MovementTypeType; title: string; subtitle: string }[] = [
  { value: 'in', title: 'Entrada', subtitle: 'Compra, devolución, producción' },
  { value: 'out', title: 'Salida', subtitle: 'Venta, consumo, envío' },
  { value: 'adjust', title: 'Ajuste', subtitle: 'Conteo físico, rotura, corrección' }
]

/** Un atributo y sus valores posibles: "Talla" → [40, 41, 42]. */
export interface ProductAttributeType {
  id?: number
  name: string
  values: string[]
  order?: number
}

/**
 * Una característica de la ficha técnica: describe el producto pero no genera
 * variante. `key` y `valueSlug` los calcula el backend al guardar, así que no se
 * mandan al crear.
 */
export interface ProductSpecType {
  key?: string
  label: string
  value: string
  valueSlug?: string
  unit?: string | null
}

/** Un paso de "cómo trabajamos": el título manda, la descripción es opcional. */
export interface ProductStepType {
  title: string
  description?: string | null
}

/** Un par ya normalizado, tal como se guarda para poder filtrar. */
export interface ProductFacetType {
  k: string
  v: string
  kl: string
  vl: string
}

/** Una característica del catálogo con los valores que se le han dado. */
export interface ProductFacetGroupType {
  key: string
  label: string
  total: number
  values: { value: string; label: string; total: number }[]
}

/** Una fila de precio: tarifa + tramo por cantidad. */
export interface ProductPriceType {
  id?: number
  variantId?: number | null
  /** Nombre de la tarifa: Público, Mayorista… */
  name: string
  /** Desde cuántas unidades aplica. 1 es el tramo base. */
  minQuantity: number | string
  amount: number | string
  isDefault?: boolean
  order?: number
}

/** Una combinación concreta de atributos. Es lo que tiene SKU y existencias. */
export interface ProductVariantType {
  id?: number
  sku?: string | null
  barcode?: string | null
  options: Record<string, string>
  /** Nulo = usa el precio del producto. */
  price?: number | null
  cost?: number | null
  stock?: number
  minStock?: number
  active?: boolean
  order?: number
  /** Excepciones de precio de esta variante, por tarifa. */
  prices?: ProductPriceType[]
}

export interface ProductType {
  id: number
  companyId: string
  kind: ProductKindType
  key: string
  name: string
  description?: string | null
  categoryId?: number | null
  category?: ParameterType | null
  sku?: string | null
  barcode?: string | null
  status: ProductStatusType
  price: number | string
  compareAtPrice?: number | string | null
  cost?: number | string | null
  taxRate: number | string
  unit: string
  trackInventory: boolean
  stock: number | string
  minStock: number | string
  images?: string[] | null
  tags?: string[] | null
  details?: string[] | null
  /** Descripción larga en HTML, la del editor de texto enriquecido. */
  content?: string | null
  /** Pasos del servicio, en orden. */
  steps?: ProductStepType[] | null
  /** Ficha técnica. */
  specs?: ProductSpecType[] | null
  /** Derivada de specs + atributos; la escribe el backend, aquí sólo se lee. */
  facets?: ProductFacetType[] | null
  order?: number
  attributes?: ProductAttributeType[]
  variants?: ProductVariantType[]
  /** Tarifas del producto (las de variante viajan dentro de cada variante). */
  prices?: ProductPriceType[]
  /** Suma de las variantes, o el saldo propio cuando no las tiene. Lo calcula el backend. */
  stockTotal?: number
  lowStock?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ProductPayloadType {
  kind: ProductKindType
  name: string
  key?: string
  description?: string | null
  categoryId?: number | null
  sku?: string | null
  barcode?: string | null
  status?: ProductStatusType
  price?: number
  compareAtPrice?: number | null
  cost?: number | null
  taxRate?: number
  unit?: string
  trackInventory?: boolean
  minStock?: number
  images?: string[]
  tags?: string[]
  details?: string[]
  content?: string | null
  steps?: ProductStepType[]
  /** Sólo lo que escribió el usuario: `key` y `valueSlug` los pone el backend. */
  specs?: { label: string; value: string; unit?: string | null }[]
  order?: number
  attributes?: ProductAttributeType[]
  variants?: ProductVariantType[]
  prices?: ProductPriceType[]
}

export interface ProductListParamsType {
  page?: number
  limit?: number
  kind?: ProductKindType
  status?: ProductStatusType
  categoryId?: number
  q?: string
  lowStock?: number
  /** Características, como `color:rojo,talla:42`. */
  specs?: string
}

export interface InventoryMovementType {
  id: number
  productId: number
  variantId?: number | null
  type: MovementTypeType
  quantity: number | string
  balance: number | string
  unitCost?: number | string | null
  reason?: string | null
  reference?: string | null
  createdAt: string
  user?: { id: number; name: string } | null
  variant?: ProductVariantType | null
  product?: ProductType | null
}

export interface MovementPayloadType {
  variantId?: number | null
  type: MovementTypeType
  quantity: number
  reason?: string | null
  reference?: string | null
  unitCost?: number | null
}

/** La API habla en números pero las columnas son `numeric` (texto): se normaliza al leer. */
/** Nombre de la tarifa que se propone al cotizar. */
export { defaultTariff, toNumber } from '@florezjaramillo/florezjaramillo-core'

/**
 * Precio de un artículo para una tarifa, una variante y una cantidad. Gana lo
 * más específico: la fila de esa variante con el mayor tramo que quepa, luego la
 * general de la tarifa, y si no hay nada, el precio base del producto.
 *
 * La regla vive en `@florezjaramillo/florezjaramillo-core` (`utils/pricing`) y la
 * comparte el backend: si divergen, la ficha diría un precio y la cotización otro.
 */
export const resolvePrice = (product: ProductType, options: { tariff?: string; variantId?: number | null; quantity?: number } = {}): number => {
  const rows: PriceRowType[] = [...(product.prices ?? []), ...(product.variants ?? []).flatMap(v => (v.prices ?? []).map(p => ({ ...p, variantId: p.variantId ?? v.id })))]
  return coreResolvePrice(rows, { basePrice: product.price, ...options })
}

/** Precio efectivo de una variante en la tarifa por defecto. */
export const variantPrice = (product: ProductType, variant: ProductVariantType): number => resolvePrice(product, { variantId: variant.id })

/** "Talla 42 · Negro", para nombrar una variante en una lista. */
export const variantLabel = (variant: ProductVariantType): string => {
  const parts = Object.values(variant.options ?? {}).filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : (variant.sku ?? 'Única')
}

/**
 * Todas las combinaciones posibles de los atributos. Es lo que propone el botón
 * de generar variantes: se calcula aquí para que la ficha no tenga que saberlo.
 */
export const buildCombinations = (attributes: ProductAttributeType[]): Record<string, string>[] => {
  const usable = attributes.filter(a => a.name.trim() && a.values.length > 0)
  if (usable.length === 0) return []

  return usable.reduce<Record<string, string>[]>(
    (acc, attribute) =>
      acc.flatMap(combo =>
        attribute.values.map(value => ({
          ...combo,
          [attribute.name]: value
        }))
      ),
    [{}]
  )
}

/** "199 cc": el valor con su unidad, si la tiene. */
export const specLabel = (spec: ProductSpecType): string => (spec.unit ? `${spec.value} ${spec.unit}` : spec.value)

/**
 * Codifica el filtro de características a `color:rojo,color:azul,talla:42`.
 *
 * Su gemelo es `parseSpecFilter` en el backend
 * (`florezjaramillo-elysia/src/services/catalog-facets.ts`): si cambia el formato,
 * hay que cambiar los dos a la vez.
 */
export const encodeSpecFilter = (filters: Record<string, string[]>): string =>
  Object.entries(filters)
    .flatMap(([key, values]) => values.map(value => `${key}:${value}`))
    .join(',')

/** Dos variantes son la misma si comparten todas sus opciones. */
export const sameOptions = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

export const productService = {
  async list(params?: ProductListParamsType) {
    const { data } = await api.get('/products', { params })
    return data.data as PaginatedResponseType<ProductType>
  },

  async get(id: number) {
    const { data } = await api.get(`/products/${id}`)
    return data.data as ProductType
  },

  /** Características ya usadas en la empresa: alimenta las sugerencias y los filtros. */
  async facets(params?: { kind?: ProductKindType }) {
    const { data } = await api.get('/products/facets', { params })
    return data.data as ProductFacetGroupType[]
  },

  async create(payload: ProductPayloadType) {
    const { data } = await api.post('/products', payload)
    return data.data as ProductType
  },

  async update(id: number, payload: Partial<ProductPayloadType>) {
    const { data } = await api.put(`/products/${id}`, payload)
    return data.data as ProductType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/products/${id}`)
    return data.data as boolean
  },

  /** Elimina varios productos de una vez. Los que tienen movimientos se archivan. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/products/bulk-delete', { ids })
    return data.data as { deleted: number; archived: number }
  },

  async movements(productId: number) {
    const { data } = await api.get(`/products/${productId}/movements`)
    return data.data as InventoryMovementType[]
  },

  async registerMovement(productId: number, payload: MovementPayloadType) {
    const { data } = await api.post(`/products/${productId}/movements`, payload)
    return data.data as InventoryMovementType
  }
}

export const inventoryService = {
  async list(lowStock?: boolean) {
    const { data } = await api.get('/inventory', { params: lowStock ? { lowStock: 1 } : undefined })
    return data.data as {
      items: ProductType[]
      meta: { total: number; lowStock: number; sinExistencias: number }
    }
  },

  async movements() {
    const { data } = await api.get('/inventory/movements')
    return data.data as InventoryMovementType[]
  }
}
